/**
 * Defensive extractor for error messages from API responses.
 *
 * The Fastify global errorHandler wraps errors as
 *   { error: { message, code, requestId } }
 * but individual routes also return flat shapes:
 *   { error: "Invalid email or password" }
 *   { message: "..." }
 *
 * Callers used to do `errorData.error || errorData.message || fallback`, which
 * stringified the wrapped envelope's object to "[object Object]". This helper
 * handles all three shapes (plus arbitrary nesting) without leaking objects
 * into UI text.
 */
export function extractApiErrorMessage(errorData: unknown, fallback: string): string {
  if (!errorData || typeof errorData !== 'object') return fallback;

  const data = errorData as Record<string, unknown>;

  // Shape 1: { error: "string" }
  if (typeof data.error === 'string' && data.error.trim().length > 0) {
    return data.error;
  }

  // Shape 2: { error: { message: "string", ... } } — Fastify wrapped envelope
  if (data.error && typeof data.error === 'object') {
    const inner = data.error as Record<string, unknown>;
    if (typeof inner.message === 'string' && inner.message.trim().length > 0) {
      return inner.message;
    }
  }

  // Shape 3: { message: "string" } — flat
  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  return fallback;
}
